import { Heart, Clock, Building2, Star, ImageOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { formatClp } from '@/lib/format/currency';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Condition = 'nuevo' | 'como_nuevo' | 'buen_estado' | 'usado';

interface ProductCardProps {
  /** Listing identifier */
  id: string;
  /** Primary photo URL. Falls back to empty-state illustration. */
  imageUrl?: string;
  /** Short listing title, max ~60 chars recommended */
  title: string;
  /** Price in CLP integer (no decimals) */
  priceCLP: number;
  /** Seller display name */
  sellerName: string;
  /** 0-5 star average */
  sellerRating: number;
  /** Item condition */
  condition: Condition;
  /** ISO 8601 timestamp of listing creation */
  createdAt: string;
  /** Whether the seller lives in the same condominium as the viewer */
  isSameBuilding?: boolean;
  /** Name of the seller's condominium — shown when NOT same building */
  condominiumName?: string;
  /** Whether the current user has favorited this listing */
  isFavorited?: boolean;
  /** Callback when the card body is clicked (navigate to detail) */
  onClick?: () => void;
  /** Callback when the heart icon is toggled */
  onToggleFavorite?: () => void;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Condition badge mapping                                            */
/* ------------------------------------------------------------------ */

const CONDITION_LABEL: Record<Condition, string> = {
  nuevo: 'Nuevo',
  como_nuevo: 'Como nuevo',
  buen_estado: 'Buen estado',
  usado: 'Usado',
};

const CONDITION_VARIANT: Record<
  Condition,
  'success' | 'primary' | 'neutral' | 'warning'
> = {
  nuevo: 'success',
  como_nuevo: 'primary',
  buen_estado: 'neutral',
  usado: 'warning',
};

/* ------------------------------------------------------------------ */
/*  Time-ago helper (lightweight, no deps)                             */
/* ------------------------------------------------------------------ */

function timeAgo(iso: string): string {
  const seconds = Math.floor(
    (Date.now() - new Date(iso).getTime()) / 1000,
  );
  if (seconds < 60) return 'hace un momento';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `hace ${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `hace ${weeks} sem`;
  const months = Math.floor(days / 30);
  return `hace ${months} mes${months > 1 ? 'es' : ''}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * ProductCard
 * -----------
 * Grid/list item for the "Mercado Vecinal" marketplace. Designed in
 * the editorial-institutional language of the Santiago Civic design
 * system: cream paper surface, hairline borders, serif display type
 * for prices, and subtle hover elevation.
 *
 * Layout (vertical card):
 * ┌─────────────────────────────┐
 * │  IMAGE (4:3 aspect ratio)   │  <- object-cover, rounded-t-md
 * │  ┌─condition─┐   ♡ heart   │  <- overlays, top-left / top-right
 * │  └───────────┘              │
 * ├─────────────────────────────┤  <- hairline border
 * │  Title (2 lines max)        │
 * │  $123.456 CLP               │  <- Fraunces display, tabular nums
 * │  ┌avatar┐ Seller  ★ 4.8    │
 * │  └──────┘                   │
 * │  🕐 hace 2h  · 🏢 Tu edif. │
 * └─────────────────────────────┘
 *
 * Hover: translateY(-2px), shadow lifts, image scales 1.03
 * Focus-visible: 2px primary outline (keyboard nav)
 * Loading: skeleton variant
 */
export function ProductCard({
  id: _id,
  imageUrl,
  title,
  priceCLP,
  sellerName,
  sellerRating,
  condition,
  createdAt,
  isSameBuilding = false,
  condominiumName,
  isFavorited = false,
  onClick,
  onToggleFavorite,
  className,
}: ProductCardProps) {
  const sellerInitials = sellerName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`${title}, ${formatClp(priceCLP)}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        'group/card relative flex flex-col overflow-hidden rounded-md',
        'border border-[var(--color-hairline)] bg-[var(--color-paper)]',
        'transition-all duration-200 ease-out cursor-pointer',
        'hover:-translate-y-0.5 hover:border-[var(--color-hairline-strong)]',
        'hover:shadow-[0_8px_30px_-8px_rgba(12,30,40,0.12)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
        className,
      )}
    >
      {/* ---- Image region ---- */}
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--color-paper-dim)]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover/card:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[var(--color-ink-faint)]">
            <ImageOff className="h-8 w-8" />
          </div>
        )}

        {/* Condition badge — top left */}
        <div className="absolute left-2.5 top-2.5">
          <Badge variant={CONDITION_VARIANT[condition]}>
            {CONDITION_LABEL[condition]}
          </Badge>
        </div>

        {/* Same-building badge — bottom left */}
        {isSameBuilding && (
          <div className="absolute bottom-2.5 left-2.5">
            <span className="inline-flex items-center gap-1 rounded-sm bg-[var(--color-primary)] px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-[var(--color-paper)] shadow-sm">
              <Building2 className="h-3 w-3" />
              Tu edificio
            </span>
          </div>
        )}

        {/* Favorite heart — top right */}
        <button
          type="button"
          aria-label={isFavorited ? 'Quitar de favoritos' : 'Agregar a favoritos'}
          aria-pressed={isFavorited}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite?.();
          }}
          className={cn(
            'absolute right-2.5 top-2.5 grid h-8 w-8 place-items-center rounded-full',
            'bg-[color-mix(in_oklch,var(--color-paper)_85%,transparent)] backdrop-blur-sm',
            'transition-all duration-150',
            'hover:bg-[var(--color-paper)] hover:scale-110',
            'focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]',
            isFavorited
              ? 'text-[var(--color-accent)]'
              : 'text-[var(--color-ink-muted)]',
          )}
        >
          <Heart
            className={cn('h-4 w-4', isFavorited && 'fill-current')}
          />
        </button>
      </div>

      {/* ---- Content region ---- */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        {/* Title — clamp to 2 lines */}
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-[var(--color-ink)]">
          {title}
        </h3>

        {/* Price — editorial serif, tabular */}
        <p className="font-display text-xl font-semibold tracking-tight text-[var(--color-ink)] tabular">
          {formatClp(priceCLP)}
        </p>

        {/* Seller row */}
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6 text-[0.5rem]">
            <AvatarFallback>{sellerInitials}</AvatarFallback>
          </Avatar>
          <span className="truncate text-xs text-[var(--color-ink-soft)]">
            {sellerName}
          </span>
          <span className="ml-auto inline-flex items-center gap-0.5 text-xs text-[var(--color-ochre)]">
            <Star className="h-3 w-3 fill-current" />
            {sellerRating.toFixed(1)}
          </span>
        </div>

        {/* Meta row — time ago + condominium */}
        <div className="mt-auto flex items-center gap-1.5 text-[0.6875rem] text-[var(--color-ink-faint)]">
          <Clock className="h-3 w-3 shrink-0" />
          <span>{timeAgo(createdAt)}</span>
          {!isSameBuilding && condominiumName && (
            <>
              <span aria-hidden className="mx-0.5">
                ·
              </span>
              <Building2 className="h-3 w-3 shrink-0" />
              <span className="truncate">{condominiumName}</span>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                           */
/* ------------------------------------------------------------------ */

export function ProductCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)]',
        className,
      )}
    >
      <Skeleton className="aspect-[4/3] w-full rounded-none" />
      <div className="space-y-3 p-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-6 w-1/2" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-6 rounded-full" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
  );
}
